import axios from "axios";
import apiUrl from "./api";

/**
 * Fetches all notifications for a given user.
 *
 * @param {string} userId - The ID of the user.
 * @returns {Promise<Object>} - The API response or error response.
 */
async function getAllNotifications(userId, pageNo = 0) {
    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.get(
            `${apiUrl}secure/notification/all-notifications?userId=${userId}&pageNumber=${pageNo}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Marks a list of notifications as read.
 *
 * @param {Array|Object} readList - An array or object representing read notification IDs.
 * @returns {Promise<Object>} - The API response or error response.
 */
async function markAsRead(readList) {
    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.patch(
            `${apiUrl}secure/notification/update-read`,
            readList,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Retrieves paginated and filtered custom notifications by title.
 *
 * @param {string} notificationTitle - The title to filter notifications.
 * @param {number} pageNo - Page number for pagination.
 * @param {number} pageSize - Number of notifications per page.
 * @returns {Promise<Object>} - The API response or error response.
 */
async function getCustomNotifications(notificationTitle, pageNo, pageSize) {
    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.get(
            `${apiUrl}secure/notification/retain-all-notification?pageNumber=${pageNo}&pageSize=${pageSize}&customNotificationTitle=${notificationTitle}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Sends a new custom notification to the server.
 *
 * @param {Object} notificationData - The notification details including message, title, type, date, and optional image.
 * @returns {Promise<Object>} - The API response or error response.
 */
async function postCustomNotifications(notificationData) {
    const token = window.localStorage.getItem('token');
    const formData = new FormData();

    // Determine image filename
    const fileName = notificationData.customNotificationImageFile?.name || `category-image-${Date.now()}.png`;

    // Append required fields
    formData.append("customNotificationMessage", notificationData.customNotificationMessage);
    formData.append("customNotificationScheduleDateTime", notificationData.customNotificationScheduleDateTime);
    formData.append("customNotificationTitle", notificationData.customNotificationTitle);
    formData.append("customNotificationType", notificationData.customNotificationType);
    formData.append("link", notificationData.link);

    // Append image if it exists
    if (notificationData.customNotificationImageFile && notificationData.customNotificationImageFile !== "") {
        formData.append("customNotificationImageFile", notificationData.customNotificationImageFile, fileName);
    }

    try {
        const response = await axios.post(
            `${apiUrl}secure/notification/add-custom-notification`,
            formData,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Updates an existing custom notification.
 *
 * @param {Object} notificationData - Updated notification data including optional image file.
 * @returns {Promise<Object>} - The API response or error response.
 */
async function updateCustomNotifications(notificationData) {
    const token = window.localStorage.getItem('token');
    const formData = new FormData();

    for (const key in notificationData) {
        if (notificationData.hasOwnProperty(key)) {
            const fileName = notificationData.customNotificationImageFile?.name || `notification-image-${Date.now()}.png`;

            if (key === "customNotificationImageFile" && notificationData.customNotificationImageFile !== "") {
                // Append image file if it exists
                formData.append("customNotificationImageFile", notificationData.customNotificationImageFile, fileName);
            } else {
                // Append other form fields
                formData.append(key, notificationData[key]);
            }
        }
    }

    try {
        const response = await axios.put(
            `${apiUrl}secure/notification/update-custom-notification`,
            formData,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Deletes a custom notification by ID.
 *
 * @param {string} notificationId - ID of the custom notification to delete.
 * @returns {Promise<Object>} - The API response or error response.
 */
async function deleteCustomNotifications(notificationId) {
    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.delete(
            `${apiUrl}secure/notification/remove-custom-notification?customNotificationId=${notificationId}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}


async function getUserListForNotification({
    search = '',
    page = 0,
    size = 10,
    startDate = '',
    endDate = new Date().toISOString().split('T')[0]
} = {}) {

    const token = window.localStorage.getItem('token');

    try {
        const response = await axios.get(
            `${apiUrl}secure/notification/user-list`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Device-Type': 'web'
                },
                params: {
                    page,
                    size,
                    search,
                    startDate,
                    endDate
                }
            }
        );

        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            };
        }
        return error.response;
    }
}






async function postCustomNotificationList(notificationData) {
    try {
        const token = window.localStorage.getItem("token");
        const formData = new FormData();

        // ✅ Format date properly
        const formatDateTime = (dateString) => {
            const date = new Date(dateString);
            const pad = (n) => n.toString().padStart(2, "0");

            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
        };

        // ✅ Basic fields
        formData.append("customNotificationTitle", notificationData.customNotificationTitle || "");
        formData.append("customNotificationMessage", notificationData.customNotificationMessage || "");
        formData.append("customNotificationType", notificationData.customNotificationType || "");

        if (notificationData.customNotificationScheduleDateTime) {
            formData.append(
                "customNotificationScheduleDateTime",
                formatDateTime(notificationData.customNotificationScheduleDateTime)
            );
        }

        // ✅ User IDs (multiple values support)
        if (Array.isArray(notificationData.userId) && notificationData.userId.length > 0) {
            notificationData.userId.forEach((id) => {
                formData.append("userIds", id);
            });
        } else {
            console.error("❌ userId missing or invalid"); 
        }

        // ✅ Link (FIXED)
        if (notificationData.link) {
            formData.append("link", notificationData.link);
        }else{
            formData.append("link", null);
        }

        // ✅ Image file (FIXED)
       if (notificationData.customNotificationImageFile !== "") {
            const file = notificationData.customNotificationImageFile;
            const fileName = file.name || `notification-image-${Date.now()}.png`;

            formData.append("customNotificationImageFile", file, fileName);
        }

        // ✅ DEBUG (correct way to see FormData)
        console.log(formData);

        // ✅ API call
        const response = await axios.post(
            `${apiUrl}secure/notification/add-custom-notification-list`,
            formData,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                },
            }
        );

        return response;

    } catch (error) {
        console.log("❌ ERROR RESPONSE:", error?.response?.data);
        return error.response;
    }
}
// Export all functions
export {
    getAllNotifications,
    markAsRead,
    getCustomNotifications,
    postCustomNotifications,
    updateCustomNotifications,
    deleteCustomNotifications,
    postCustomNotificationList,
    getUserListForNotification};
